import { ReactComponent as IconSearch } from "@/assets/svgs/icon-search.svg";
import Dropdown from "@/components/Dropdown/index";
import useModal from "@/hooks/use-modal";
import { useState, useMemo } from "react";
import AddNewAddressModal from "./add-new-address-modal";
import PropTypes from "prop-types";
import Search from '@/componentsDS/Search/index';
import { feature, features } from "@/utils/feature-verifier";

const DropdownTab = ({ children, selected, onClick }) => (
	<button
		className={`etr__record-details__dropdown__tab ${selected ? "etr__record-details__dropdown__tab--selected" : ""
			}`}
		onClick={onClick}
	>
		{children}
	</button>
);

DropdownTab.propTypes = {
	children: PropTypes.node.isRequired,
	selected: PropTypes.bool.isRequired,
	onClick: PropTypes.func.isRequired,
};

const AddressDropdown = ({
	title,
	selectedAddress,
	setSelectedAddress,
	availableAddresses,
}) => {
	const [newAddress, setNewAddress] = useState(null);
	const [filterValue, setFilterValue] = useState("");
	const [selectedTab, setSelectedTab] = useState("All");
	const { visible: showAddAddress, hide, show } = useModal();

	const displaySelectedAddress = useMemo(() => {
		if (!selectedAddress) return "";

		if (selectedAddress.email) {
			return `${selectedAddress.name} - ${selectedAddress.email}`;
		}

		return selectedAddress.name;
	}, [selectedAddress]);

	// Update new address and set it as the selected address
	const updateNewAddressAndSetSelected = (data) => {
		setNewAddress(data);
		setSelectedAddress(data);
	};

	// Handle selected address
	const handledSelectedAddress = (item) => {
		if (item.type === "saved") {
			const address = {
				name: item.name,
				email: item.email,
				savedAddressId: item.id,
				walletAddress: item.walletAddress
			};
			setSelectedAddress(address);
		}
		else if (item.type === "pitstop") {
			const address = {
				name: item.name,
				email: item.email,
				orgId: item.id,
				walletAddress: item.walletAddress
			};
			setSelectedAddress(address);
		}
	}

	// Filtered and categorized dropdown items based on filterValue and selectedTab
	const filteredDropdownItems = useMemo(() => {
		return availableAddresses.filter((item) => {
			let filtered = true;
			if (selectedTab !== "All") {
				filtered = item.type === selectedTab;
			}
			if (filterValue !== "" && filtered) {
				filtered =
					item.name
						.toLowerCase()
						.includes(filterValue.toLowerCase()) ||
					item.email
						?.toLowerCase()
						.includes(filterValue.toLowerCase());
			}
			return filtered;
		});
	}, [availableAddresses, filterValue, selectedTab]);

	const addressList = filteredDropdownItems.map((item) => (
		<Dropdown.Item
			key={item.id}
			onClick={() => handledSelectedAddress(item)}
		>
			<div
				className={
					(selectedAddress?.savedAddressId
						?? selectedAddress?.orgId) === item.id
						? "etr__record-details__address__list__item-active"
						: "etr__record-details__address__list__item"
				}
			>
				<span className="etr__record-details__span">{item.name}</span>
				{item.email && (
					<span className="etr__record-details__span">{item.email}</span>
				)}
			</div>
		</Dropdown.Item>
	));

	return (
		<div className="etr__record-details__section__content__grid__item">
			<label
				id="dropdown-label"
				htmlFor="etr-dropdown"
				className="etr__record-details__dropdown__label"
			>
				* {title}
			</label>
			<Dropdown
				placeholder="Select Option"
				style={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					gap: "8px",
				}}
				value={displaySelectedAddress}
				aria-labelledby="dropdown-label"
				id="etr-dropdown"
			>
				<div style={{padding: '0 16px 4px 16px'}}>
					<Search
						placeholder="Search"
						value={filterValue}
						onChange={(e) => setFilterValue(e.target.value)}
						onCancel={() => setFilterValue('')}
						variant='filled'
					/>
				</div>
				<div className="etr__record-details__divider"></div>
				<div className="etr__record-details__dropdown__tabs">
					Addresses:
					<DropdownTab
						selected={selectedTab === "All"}
						onClick={() => setSelectedTab("All")}
					>
						All
					</DropdownTab>
					<DropdownTab
						selected={selectedTab === "pitstop"}
						onClick={() => setSelectedTab("pitstop")}
					>
						Pitstop
					</DropdownTab>
					<DropdownTab
						selected={selectedTab === "saved"}
						onClick={() => setSelectedTab("saved")}
					>
						Saved
					</DropdownTab>
				</div>
				<div className="etr__record-details__divider"></div>
				<div className="etr__record-details__address__list">
					{filteredDropdownItems.length === 0 && (
						<div className="etr__record-details__address__list__no-items">
							No Matching Addresses Found
						</div>
					)}
					{addressList}
				</div>
				<div className="etr__record-details__divider"></div>
				<div
					className="etr__record-details__new-address_section"
					id="etr-new-address-section"
				>
					<span>New Address</span>
					{newAddress && (
						<Dropdown.Item onClick={show}>Edit</Dropdown.Item>
					)}
				</div>
				{newAddress ? (
					<Dropdown.Item
						key={newAddress.name}
						onClick={() => setSelectedAddress(newAddress)}
					>
						<div className="etr__record-details__new-address__list__item">
							<span className="etr__record-details__span">
								{newAddress.name}
							</span>
							{newAddress.email && (
								<span className="etr__record-details__span">
									{newAddress.email}
								</span>
							)}
						</div>
					</Dropdown.Item>
				) : (
					<Dropdown.Item onClick={show}>
						<div style={{ padding: "8px" }}>+ Add New Address</div>
					</Dropdown.Item>
				)}
			</Dropdown>
			<AddNewAddressModal
				visible={showAddAddress}
				hide={hide}
				newAddress={newAddress}
				setNewAddress={updateNewAddressAndSetSelected}
				availableAddresses={availableAddresses}
			/>
		</div>
	);
};

AddressDropdown.propTypes = {
	title: PropTypes.string.isRequired,
	selectedAddress: PropTypes.object,
	setSelectedAddress: PropTypes.func.isRequired,
	availableAddresses: PropTypes.array.isRequired,
};

export default AddressDropdown;
